import { groupBy, values } from 'lodash-es';
import moment from 'moment';

import { CSSColors } from 'Components/Colors';
import { DueDiligenceDashboardCompletedRiskAssessment, DueDiligenceDashboardRiskType } from 'Models/Dashboards';
import { Effectiveness, effectivenessAsString, numberAsEffectivenessString } from 'Models/OperationalControls';
import { RiskRating, numberAsRiskRatingString, riskRatingAsString } from 'Models/RiskRating';
import { getEffectivenessChartColor } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

// chart styles

export const getRiskRatingChartColor = (riskRating: RiskRating): CSSColors => {
    switch (riskRating) {
        case RiskRating.UNRATED:
            return CSSColors.DARK_GRAY;
        case RiskRating.LOW:
            return CSSColors.DARK_GREEN;
        case RiskRating.LOW_MODERATE:
            return CSSColors.LIGHT_GREEN;
        case RiskRating.MODERATE:
            return CSSColors.YELLOW;
        case RiskRating.MODERATE_HIGH:
            return CSSColors.ORANGE;
        case RiskRating.HIGH:
            return CSSColors.RED;
    }
};

// data transformers

const mapUndefinedRiskRatingOrEffectivenessScoresToZero = (scores: (number | undefined)[]): number[] => scores.map((score) => (score === undefined ? 0 : score));
const filterUnratedRiskRatingsOrEffectivenessScores = (scores: (number | undefined)[]): number[] => scores.filter((score) => score !== undefined && score !== 0) as number[];

export const calculateRiskRatingOrEffectivenessAverage = (values: (number | undefined)[]): number => {
    const ratedValues = filterUnratedRiskRatingsOrEffectivenessScores(values);
    if (ratedValues.length === 0) {
        return 0;
    }

    return ratedValues.reduce((acc, val) => (acc += val), 0) / ratedValues.length;
};

interface RiskRatingsDataArray {
    value: number;
    name: string;
    color: CSSColors;
}

/**
 * Groups risk ratings into named and color-coded groups.
 */
export const buildRiskRatingDataArray = (scores: number[]): RiskRatingsDataArray[] => {
    const allPossibleRiskRatings = [RiskRating.UNRATED, RiskRating.LOW, RiskRating.LOW_MODERATE, RiskRating.MODERATE, RiskRating.MODERATE_HIGH, RiskRating.HIGH];
    const getScoreCount = (searchValue: RiskRating) => scores.filter((score: number) => searchValue === score).length;

    return allPossibleRiskRatings.map((riskRatingValue: RiskRating) => ({
        value: getScoreCount(riskRatingValue),
        name: riskRatingAsString(riskRatingValue),
        color: getRiskRatingChartColor(riskRatingValue),
    }));
};

export interface RiskRatingsData {
    data: RiskRatingsDataArray[];
    numberOfScoredAssessmentSubjects: number;
    numberOfAssessmentSubjects: number;
    average: number;
    averageLabel: string;
}

/**
 * Calculates an average risk rating and the number of "rated" scores, and groups risk ratings into named and color-coded groups.
 */
export const buildRiskRatingsChartData = (scores: (number | undefined)[], numberOfAssessmentSubjects: number): RiskRatingsData => {
    const average = calculateRiskRatingOrEffectivenessAverage(scores);
    const ratedScores = filterUnratedRiskRatingsOrEffectivenessScores(scores);
    const numberOfScoredAssessmentSubjects = ratedScores.length;

    return { data: buildRiskRatingDataArray(mapUndefinedRiskRatingOrEffectivenessScoresToZero(scores)), numberOfScoredAssessmentSubjects: numberOfScoredAssessmentSubjects, numberOfAssessmentSubjects: numberOfAssessmentSubjects, average, averageLabel: numberAsRiskRatingString(average) };
};

export const buildEffectivenessDataArray = (scores: number[]): RiskRatingsDataArray[] => {
    const allPossibleEffectivenessRatings = [Effectiveness.UNRATED, Effectiveness.FAIL, Effectiveness.WEAK, Effectiveness.MODERATE, Effectiveness.STRONG, Effectiveness.ROBUST];
    const getScoreCount = (searchValue: Effectiveness) => scores.filter((score: number) => searchValue === score).length;

    return allPossibleEffectivenessRatings.map((effectivenessValue: Effectiveness) => ({
        value: getScoreCount(effectivenessValue),
        name: effectivenessAsString(effectivenessValue),
        color: getEffectivenessChartColor(effectivenessValue),
    }));
};

/**
 * Calculates an average effectiveness rating and the number of "rated" scores, and groups effectiveness ratings into named and color-coded groups.
 */
export const buildEffectivenessChartData = (scores: (number | undefined)[], numberOfAssessmentSubjects: number): RiskRatingsData => {
    const average = calculateRiskRatingOrEffectivenessAverage(scores);
    const ratedScores = filterUnratedRiskRatingsOrEffectivenessScores(scores);
    const numberOfScoredAssessmentSubjects = ratedScores.length;

    return { data: buildEffectivenessDataArray(mapUndefinedRiskRatingOrEffectivenessScoresToZero(scores)), numberOfScoredAssessmentSubjects: numberOfScoredAssessmentSubjects, numberOfAssessmentSubjects: numberOfAssessmentSubjects, average, averageLabel: numberAsRiskRatingString(average) };
};

export interface AssessmentsBarChartDataInput {
    monthKey: string; // formatted string key "MMM YY" or "overdue"
    riskScore?: number;
    completedOnTime?: boolean;
}

export interface AssessmentsBarChartDataRow {
    data: number[];
    name: string;
    color: CSSColors;
}

export const buildAssessmentsBarChartData = (assessments: AssessmentsBarChartDataInput[], months: string[]): number[] => {
    return months.map((month) => assessments.filter((assessment) => assessment.monthKey === month).length);
};

export const getPercentageCompletedOnTime = (assessments: AssessmentsBarChartDataInput[], months: string[]): number[] => {
    return months.map((month) => {
        const currentMonthAssessments = assessments.filter((assessment) => assessment.monthKey === month);
        return currentMonthAssessments.length === 0 ? 100 : (currentMonthAssessments.filter((assessment) => assessment.completedOnTime).length / currentMonthAssessments.length) * 100;
    });
};

export const buildAssessmentsBarChartByMonth = (assessments: AssessmentsBarChartDataInput[], riskType: DueDiligenceDashboardRiskType, months: string[]): AssessmentsBarChartDataRow[] => {
    const displayValues: number[] = riskType === DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS ? [Effectiveness.UNRATED, Effectiveness.FAIL, Effectiveness.WEAK, Effectiveness.MODERATE, Effectiveness.STRONG, Effectiveness.ROBUST] : [RiskRating.UNRATED, RiskRating.HIGH, RiskRating.MODERATE_HIGH, RiskRating.MODERATE, RiskRating.LOW_MODERATE, RiskRating.LOW];
    return displayValues.map((displayValue: number) => ({
        data: buildAssessmentsBarChartData(
            assessments.filter((assessment) => assessment.riskScore === displayValue),
            months
        ),
        name: riskType === DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS ? numberAsEffectivenessString(displayValue) : riskRatingAsString(displayValue),
        color: riskType === DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS ? getEffectivenessChartColor(displayValue) : getRiskRatingChartColor(displayValue),
    }));
};

/**
 * Given an array of historical due diligence workflows, removes any workflows that are superseded by later workflows of the same assessment subject in the same month.
 * The rationale for doing this is that if a client completes a workflow for an assessment subject multiple times in a short period of time, it is most likely because a mistake was made or new information was brought to light that invalidates the earlier workflow(s).
 * Returns a new array.
 */
export const onlyMostRecentAssessmentsPerMonth = (assessments: DueDiligenceDashboardCompletedRiskAssessment[]): DueDiligenceDashboardCompletedRiskAssessment[] => {
    const assessmentsByMonth = groupBy(assessments, (assessment) => {
        const completedDate = moment(assessment.completed_time);
        return `${completedDate.year().toString()} ${completedDate.month().toString()}`;
    });

    const latestAssessments: DueDiligenceDashboardCompletedRiskAssessment[] = [];
    values(assessmentsByMonth).forEach((assessmentsForMonth) => {
        const assessmentsByAssessmentSubject = groupBy(assessmentsForMonth, (assessment) => assessment.service_id);

        values(assessmentsByAssessmentSubject).forEach((assessmentsForAssessmentSubject) => {
            const mostRecentAssessmentForAssessmentSubject = assessmentsForAssessmentSubject.reduce((latest, assessment) => (assessment.completed_time > latest.completed_time ? assessment : latest));
            latestAssessments.push(mostRecentAssessmentForAssessmentSubject);
        });
    });

    return latestAssessments;
};
